/**
 * 拖拽状态机（engine 层，通用，纯函数）：不碰 DOM/PointerEvent，输入输出都是普通数字/坐标，
 * 方便 vitest 直接测（照抄 chrome-chemistry drag-controller.ts 的这条设计原则）。
 * DOM 事件→世界坐标的转换、Pointer Capture 等浏览器相关的部分交给
 * `pointer-controller.ts`；jsdom 不支持 PointerEvent/SVG 几何 API（已验证），
 * 把浏览器相关代码限制在薄薄一层，能保证核心拖拽逻辑不依赖不可测的 API。
 *
 * 只实现两种模式（比 chrome-chemistry 的通用锚点吸附系统简单很多——本工程当前没有
 * "装配关系图"需求）：离散放置（砝码拖到放置区）、连续取值（测力计拉环拖成力值）。
 */

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** 拖拽起手阈值（像素）：位移超过它才算真正拖拽，防止手抖误触发（照抄原型 3px 档）。 */
export const DRAG_THRESHOLD_PX = 4;

export function hasExceededDragThreshold(start: Point, current: Point): boolean {
  return Math.hypot(current.x - start.x, current.y - start.y) > DRAG_THRESHOLD_PX;
}

export function isPointInRect(point: Point, rect: Rect): boolean {
  return (
    point.x >= rect.x &&
    point.x <= rect.x + rect.width &&
    point.y >= rect.y &&
    point.y <= rect.y + rect.height
  );
}

/** 力值拖拽会话：记录起手时的指针位置与起始力值，供增量映射。 */
export interface ForceDragSession {
  startPoint: Point;
  startForce: number;
}

/**
 * 每世界坐标单位映射的力值增量（N/unit）。原型 ui.js 的 `0.02` 档位是按它自己 0-5N
 * 量程标定的；本工程 weightCount 最多堆到 3 个砝码、接触面最粗糙到毛巾
 * （`computeMaxStaticFriction(25, "towel")` ≈ 19.5N），量程/灵敏度必须按本工程真实的
 * 最大静摩擦范围重新标定，否则拖满全程也拉不动木块（见 scene-manager.ts 的 `FORCE_MAX=26`——
 * 比最坏组合 19.5N 留 33% 余量，不能贴着最坏组合走，余量太窄会让"刚好拉动"和"撞量程上限"在
 * 人手拖拽精度下分不清，真机反馈"19.1N 木块还没动"就是踩在这条坑上）。
 */
export const FORCE_DRAG_SENSITIVITY = 0.06;

export function beginForceDrag(startPoint: Point, currentForce: number): ForceDragSession {
  return { startPoint, startForce: currentForce };
}

/**
 * 力值量化到 0.1N（照抄原型分度值），并夹在 [0, forceMax] 区间内。
 * `sensitivity` 可选覆盖——测力计拖拽用默认的 `FORCE_DRAG_SENSITIVITY`（0-26N 量程标定），
 * 光学场景的距离拖拽（10-60cm 量程，跨度是力值量程的数倍）复用同一套会话状态机时需要一个更大
 * 的灵敏度，否则按力值的标定拖满全程也移不动几厘米（todo/068 M2）。不传时行为与原来完全一致。
 */
export function computeDraggedForce(
  session: ForceDragSession,
  point: Point,
  forceMax: number,
  sensitivity: number = FORCE_DRAG_SENSITIVITY,
): number {
  const raw = session.startForce + (point.x - session.startPoint.x) * sensitivity;
  const quantized = Math.round(raw * 10) / 10;
  return Math.min(forceMax, Math.max(0, quantized));
}


